import React, { useState } from "react";
import api from "../services/api";
import { toast } from "react-hot-toast";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface FlagVideoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  videoId: string;
  videoUrl: string;
}

// Issue type options
const ISSUE_TYPES = [
  { id: "video_issue", label: "Video Issue" },
  { id: "audio_issue", label: "Audio Issue" },
];

const FlagVideoDialog: React.FC<FlagVideoDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  videoId,
  videoUrl,
}) => {
  const [selectedIssueType, setSelectedIssueType] = useState<string>("");
  const [additionalNotes, setAdditionalNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedIssueType) {
      toast.error("Please select an issue type");
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post("/api/videos/feedback", {
        project_id: projectId,
        video_id: videoId,
        video_url: videoUrl,
        issue_type: selectedIssueType,
        additional_notes: additionalNotes,
      });

      toast.success("Feedback submitted successfully");

      // Reset form and close dialog
      setSelectedIssueType("");
      setAdditionalNotes("");
      onClose();
    } catch (error) {
      console.error("Error submitting video feedback:", error);
      toast.error("Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-md p-6 mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        <div className="absolute top-3 right-3">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:cursor-pointer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
          Report Video Issue
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Select issue type:
            </label>
            <select
              value={selectedIssueType}
              onChange={(e) => setSelectedIssueType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            >
              <option value="">Select an issue type</option>
              {ISSUE_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Additional notes (optional):
            </label>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-24 resize-none"
              placeholder="Please provide any additional details about the issue..."
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 mr-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors hover:cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FlagVideoDialog;
